/**
 * Conversation List Screen
 * Shows the user's recent conversations and a button to start a new one.
 */

import React, { useCallback } from 'react';
import {
  FlatList,
  TouchableOpacity,
  StyleSheet,
  View,
  Text,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import Ionicons from '../components/ui/Ionicons';

import { colors } from '../theme';
import { FloatingCard } from '../components/ui/FloatingCard';
import { ScreenHeader } from '../components/ui/ScreenHeader';
import { LoadingState, ErrorState, EmptyState } from '../components/ui';
import { api } from '../services/api';
import { useCourseStore } from '../store/courseStore';
import { createLogger } from '../utils/logger';
import { useDisplayTheme } from '../hooks/useDisplayTheme';
import { useAsyncAction } from '../hooks/useAsyncAction';
import { useNetworkRegain } from '../hooks/useNetworkRegain';

const log = createLogger('ConversationList');

interface SessionSummary {
  id: string;
  title: string | null;
  flowType: string;
  courseId: string;
  sourceLocale: string;
  targetLocale: string;
  role: string;
  messageCount: number;
  createdAt: string;
  updatedAt: string;
}

const FLOW_TYPE_DISPLAY: Record<string, { emoji: string; label: string }> = {
  pronunciation: { emoji: '🎙️', label: 'Pronunciation' },
  conversation: { emoji: '💬', label: 'Conversation' },
  grammar: { emoji: '📝', label: 'Grammar' },
  roleplay_generator: { emoji: '🎭', label: 'Roleplay' },
  roleplay_runner: { emoji: '🎭', label: 'Roleplay' },
  quick_question: { emoji: '❓', label: 'Quick Question' },
};

const MAX_SESSIONS = 20;

type RelativeTime =
  | { key: 'justNow' }
  | { key: 'minutesAgo' | 'hoursAgo' | 'daysAgo'; count: number };

function getRelativeTime(dateString: string): RelativeTime | null {
  const diffMs = Date.now() - new Date(dateString).getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHr = Math.floor(diffMs / 3_600_000);
  const diffDay = Math.floor(diffMs / 86_400_000);

  if (diffMin < 1) return { key: 'justNow' };
  if (diffMin < 60) return { key: 'minutesAgo', count: diffMin };
  if (diffHr < 24) return { key: 'hoursAgo', count: diffHr };
  if (diffDay < 30) return { key: 'daysAgo', count: diffDay };
  return null;
}

// ── Conversation Card ──────────────────────────────────────────────

/** Format course badge label from base languages (e.g., "ES→EN") */
function courseBadgeLabel(sourceLocale: string, targetLocale: string): string {
  const src = sourceLocale.split('-')[0].toUpperCase();
  const tgt = targetLocale.split('-')[0].toUpperCase();
  return `${src}→${tgt}`;
}

interface ConversationCardProps {
  session: SessionSummary;
  showCourseBadge: boolean;
  onPress: () => void;
}

const ConversationCard = React.memo(function ConversationCard({
  session,
  showCourseBadge,
  onPress,
}: ConversationCardProps) {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();

  const title = session.title || t('conversations.untitled');
  const rel = getRelativeTime(session.updatedAt);
  let time: string;
  if (!rel) {
    time = new Date(session.updatedAt).toLocaleDateString();
  } else if (rel.key === 'justNow') {
    time = t('conversations.justNow');
  } else if (rel.key === 'minutesAgo') {
    time = t('conversations.minutesAgo', { count: rel.count });
  } else if (rel.key === 'hoursAgo') {
    time = t('conversations.hoursAgo', { count: rel.count });
  } else {
    time = t('conversations.daysAgo', { count: rel.count });
  }

  return (
    <TouchableOpacity
      testID={`conversations-card-${session.id}`}
      accessibilityRole="button"
      accessibilityLabel={`${title}, ${t('conversations.messageCount', { count: session.messageCount })}, ${time}`}
      onPress={onPress}
      activeOpacity={0.7}
    >
      <FloatingCard mt="$3">
        <View style={styles.cardRow}>
          <View style={styles.cardContent}>
            <Text
              allowFontScaling={false}
              numberOfLines={1}
              style={{
                fontSize: 16 * fontScale,
                fontWeight: '600',
                color: theme.text,
                ...ffs,
              }}
            >
              {title}
            </Text>
            <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 4, gap: 6 }}>
              {showCourseBadge && (
                <Text
                  allowFontScaling={false}
                  style={{
                    fontSize: 11 * fontScale,
                    color: theme.textSecondary,
                    backgroundColor: theme.isDark ? '#2A2A2A' : '#F0F0F0',
                    paddingHorizontal: 6,
                    paddingVertical: 1,
                    borderRadius: 4,
                    overflow: 'hidden',
                    fontWeight: '600',
                    ...ffs,
                  }}
                >
                  {courseBadgeLabel(session.sourceLocale, session.targetLocale)}
                </Text>
              )}
              {session.flowType && FLOW_TYPE_DISPLAY[session.flowType] && (
                <Text
                  allowFontScaling={false}
                  style={{
                    fontSize: 11 * fontScale,
                    color: theme.primary,
                    backgroundColor: theme.primarySoft,
                    paddingHorizontal: 6,
                    paddingVertical: 1,
                    borderRadius: 4,
                    overflow: 'hidden',
                    ...ffs,
                  }}
                >
                  {FLOW_TYPE_DISPLAY[session.flowType].emoji} {FLOW_TYPE_DISPLAY[session.flowType].label}
                </Text>
              )}
              <Text
                allowFontScaling={false}
                style={{
                  fontSize: 13 * fontScale,
                  color: theme.textSecondary,
                  ...ffs,
                }}
              >
                {t('conversations.messageCount', { count: session.messageCount })} · {time}
              </Text>
            </View>
          </View>
          <Ionicons name="chevron-forward" size={20 * fontScale} color={theme.textSecondary} />
        </View>
      </FloatingCard>
    </TouchableOpacity>
  );
});

// ── Screen ─────────────────────────────────────────────────────────

export function ConversationListScreen() {
  const { t } = useTranslation();
  const { theme } = useDisplayTheme();
  const navigation = useNavigation('ConversationList');
  const { bottom: bottomInset } = useSafeAreaInsets();

  const fetchSessionsFn = useCallback(async () => {
    const result = await api.listSessions({ params: { limit: MAX_SESSIONS } });
    if (result.ok) {
      return result.data.sessions as unknown as SessionSummary[];
    }
    log.error('Failed to fetch sessions', { kind: result.kind, error: result.error });
    throw new Error(t('common.error'));
  }, [t]);

  const { execute: fetchSessions, data: sessions, loading, error } = useAsyncAction(fetchSessionsFn);

  useFocusEffect(
    useCallback(() => {
      fetchSessions().catch(() => {});
      return () => {};
    }, [fetchSessions])
  );

  // Recover the list after a network transition — a fetch that failed during
  // the outage won't re-fire on its own until the screen is re-focused.
  useNetworkRegain(
    useCallback(() => {
      fetchSessions().catch(() => {});
    }, [fetchSessions])
  );

  const handleSelectSession = useCallback(
    (sessionId: string) => {
      navigation.navigate('Chat', { sessionId });
    },
    [navigation]
  );

  const handleNewConversation = useCallback(() => {
    navigation.navigate('Chat', { sessionId: '' });
  }, [navigation]);


  const courses = useCourseStore((s) => s.courses);
  const hasMultipleCourses = courses.length > 1;

  const renderItem = useCallback(
    ({ item }: { item: SessionSummary }) => (
      <ConversationCard
        session={item}
        showCourseBadge={hasMultipleCourses}
        onPress={() => handleSelectSession(item.id)}
      />
    ),
    [handleSelectSession, hasMultipleCourses]
  );

  const keyExtractor = useCallback((item: SessionSummary) => item.id, []);

  return (
    <View style={{ flex: 1, backgroundColor: theme.background }}>
      <ScreenHeader
        title={t('conversations.title')}
        headerRight={
          <TouchableOpacity
            testID="conversations-button-new"
            accessibilityRole="button"
            accessibilityLabel={t('conversations.newConversation')}
            onPress={handleNewConversation}
            style={{ width: 44, height: 44, alignItems: 'center', justifyContent: 'center' }}
          >
            <Ionicons name="add-circle-outline" size={28} color={colors.primary} />
          </TouchableOpacity>
        }
      />
      {/* Content */}
      {loading || (sessions === null && !error) ? (
        <View style={styles.centered}>
          <LoadingState testID="conversations-loading" />
        </View>
      ) : error ? (
        <View style={styles.centered}>
          <ErrorState
            testID="conversations-error"
            message={error}
            retryLabel={t('common.retry')}
            onRetry={() => { fetchSessions().catch(() => {}); }}
          />
        </View>
      ) : !sessions || sessions.length === 0 ? (
        <View style={styles.centered}>
          <EmptyState
            testID="conversations-empty"
            message={t('conversations.empty')}
            action={{
              label: t('conversations.newConversation'),
              onPress: handleNewConversation,
            }}
          />
        </View>
      ) : (
        <FlatList
          data={sessions}
          renderItem={renderItem}
          keyExtractor={keyExtractor}
          contentContainerStyle={[styles.list, { paddingBottom: 8 + bottomInset }]}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 40,
  },
  list: {
    paddingHorizontal: 4,
  },
  cardRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  cardContent: {
    flex: 1,
  },
});
